import React, { useState, useRef, useEffect } from 'react';
import { askCandidateChatbot } from '../../../services/candidate/api';
import { Bot } from 'lucide-react';
import './CandidateChatbot.css';

const categoryIcons = {
  "background verification": "🔍",
  "document submission": "📜",
  "education verification": "🎓",
  "employment verification": "💼",
  "bgv results & issues": "⚠️"
};

const CandidateChatbot = () => {
  const [messages, setMessages] = useState([
    { from: 'bot', text: 'Hi! 🤗 I am your BGV Assistant. How can I help you today?' },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, suggestedQuestions]);

  const simulateTypingDelay = (callback) => {
    setLoading(true);
    setTimeout(() => {
      callback();
      setLoading(false);
    }, 500); // 500ms delay for human-like effect
  };

  const sendMessage = async (text) => {
    if (!text.trim()) return;

    // 1️⃣ Add user message immediately
    const newUserMessage = { from: 'user', text };
    setMessages((prev) => [...prev, newUserMessage]);

    setInput('');
    simulateTypingDelay(async () => {
      try {
        const token = localStorage.getItem('bgc_token');
        const data = await askCandidateChatbot(text,token);
        const answer = data?.answer || 'Hmm 🤔 I’m not sure about that. Please check with HR.';
        
        // 2️⃣ Add bot message after delay
        const newBotMessage = { from: 'bot', text: answer };
        setMessages((prev) => [...prev, newBotMessage]);

        // 3️⃣ Show category chips or clear them
        if (data?.questions?.length) {
          setSuggestedQuestions(data.questions);
        } else {
          setSuggestedQuestions([]);
        }
      } catch (error) {
        setMessages((prev) => [...prev, { from: 'bot', text: '⚠️ Something went wrong. Please try again later.' }]);
        setSuggestedQuestions([]);
      }
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') sendMessage(input);
  };

  const selectSuggestedQuestion = async (questionText) => {
    await sendMessage(questionText);
  };

  return (
    <div className="chatbot-container-inner">
      <div className="chatbot-messages">
        {messages.map((msg, idx) => (
          <div key={idx} className={`chat-message ${msg.from}`}>
            {msg.from === 'bot' && <span className="bot-avatar">🤖</span>}
            {msg.text.split('\n').map((line, i) => (
              <div
                key={i}
                className="bot-message-line"
                dangerouslySetInnerHTML={{
                  __html: line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                }}
              />
            ))}
          </div>
        ))}
        {suggestedQuestions.length > 0 && (
          <div className="suggested-questions-scroll">
            {suggestedQuestions.map((q, idx) => {
              const icon = categoryIcons[q.toLowerCase()] || '❓';
              return (
                <button
                  key={idx}
                  className="suggested-question-chip"
                  onClick={() => selectSuggestedQuestion(q)}
                >
                  {icon} {q}
                </button>
              );
            })}
          </div>
        )}

        {loading && (
          <div className="chat-message bot typing-indicator">
            <span className="bot-avatar">🤖</span>
            <span className="dots">
              <span>.</span><span>.</span><span>.</span>
            </span>
          </div>
        )}

        <div ref={chatEndRef}></div>
      </div>

      <div className="chatbot-input-container">
        <input
          type="text"
          placeholder="Type your question..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
        />
        <button onClick={() => sendMessage(input)}>Send</button>
      </div>
    </div>
  );
};

export default CandidateChatbot;